import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Users, CalendarDays, ShoppingBag, ReceiptText, RotateCcw, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import WriteButton from "@/components/WriteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

const formatDate = (value, withTime = false) => value
  ? new Date(value).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "long", year: "numeric", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) })
  : "Belum diketahui";

const membershipDuration = (value) => {
  if (!value) return "";
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000));
  if (days < 31) return `${days} hari`;
  if (days < 365) return `${Math.floor(days / 30)} bulan`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return `${years} tahun${months ? ` ${months} bulan` : ""}`;
};

import ReportActions from '@/components/ReportActions';
import ContactEditDialog from '@/components/ContactEditDialog';

export default function CustomerManagement() {
  const navigate = useNavigate();
  const { readOnly } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileSearch, setProfileSearch] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const term = search.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const { data } = await api.get("/customers", { params: term ? { q: term } : {}, signal: controller.signal });
        if (!controller.signal.aborted) setCustomers(data);
      } catch (e) {
        if (!controller.signal.aborted) setError(apiError(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [search, revision]);

  useEffect(() => {
    if (!profileCustomer) return;
    const controller = new AbortController();
    setProfileLoading(true);
    setProfileError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/${profileCustomer.id}/profile`, {
          params: profileSearch.trim() ? { q: profileSearch.trim(), limit: 500 } : { limit: 100 },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setProfile(data);
      } catch (error) {
        if (!controller.signal.aborted) setProfileError(apiError(error));
      } finally {
        if (!controller.signal.aborted) setProfileLoading(false);
      }
    }, profileSearch ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [profileCustomer, profileSearch]);

  const openProfile = (customer) => {
    setProfileCustomer(customer);
    setProfile(null);
    setProfileSearch("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (readOnly || saving) return;
    if (!form.name.trim()) return toast.error("Nama pelanggan wajib diisi");
    setSaving(true);
    try {
      await api.post("/customers", { name: form.name.trim(), phone: form.phone.trim() || null });
      toast.success("Pelanggan ditambahkan");
      setOpen(false);
      setSearch("");
      setRevision((value) => value + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card data-testid="customer-management-card" className="min-w-0 self-start bg-ui-card border-ui-line text-ui-text shadow-none">
        <CardHeader className="p-4 border-b border-ui-line">
          <ReportActions title="Daftar Pelanggan" disabled={loading || !!error} headers={['Nama Pelanggan','Telepon','Sisa Piutang']} rows={customers.map(c => [c.name,c.phone,c.debt])} notes={[`Pencarian: ${search || 'Semua'}`, 'Mengikuti daftar pelanggan yang sudah dimuat pada layar.']} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Users className="h-5 w-5 text-brand-readable" /> Pelanggan</h2>
              <CardDescription className="text-ui-muted mt-1">Daftar pelanggan toko dan sisa piutang.</CardDescription>
            </div>
            <WriteButton data-testid="add-customer-btn" onClick={() => { setForm({ name: "", phone: "" }); setOpen(true); }} className="bg-brand-button hover:bg-brand-hover text-ui-text gap-2">
              <Plus className="h-4 w-4" /> Tambah Pelanggan
            </WriteButton>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
            <Input data-testid="users-customer-search" aria-label="Cari pelanggan berdasarkan nama atau nomor HP" placeholder="Cari nama / no. HP pelanggan…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-ui-card border-ui-line" />
          </div>
          {loading ? (
            <p role="status" className="py-6 text-center text-sm text-ui-muted">Memuat pelanggan…</p>
          ) : error ? (
            <div role="alert" className="space-y-3 rounded-lg border border-danger bg-danger-soft p-4 text-sm text-danger-readable">
              <p>{error}</p>
              <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button>
            </div>
          ) : customers.length === 0 ? (
            <p data-testid="customers-empty" className="py-6 text-center text-sm text-ui-muted">{search.trim() ? "Pelanggan tidak ditemukan." : "Belum ada pelanggan. Tambahkan pelanggan pertama Anda."}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="customers-table">
                  <thead className="text-xs text-ui-muted uppercase tracking-wide">
                    <tr><th scope="col" className="text-left pb-3 pr-3">Nama / No. HP</th><th scope="col" className="text-right pb-3">Piutang</th><th scope="col" className="w-8"><span className="sr-only">Profil</span></th></tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id} data-testid={`customer-row-${customer.id}`} onClick={() => openProfile(customer)} className="border-t border-ui-line hover:bg-ui-soft cursor-pointer">
                        <td className="py-3 pr-3"><div className="font-medium break-words">{customer.name}</div><div className="text-xs text-ui-muted mt-1 break-all">{customer.phone || "No. HP belum diisi"}</div></td>
                        <td className={`py-3 text-right font-mono whitespace-nowrap ${customer.debt > 0 ? "text-danger-readable" : "text-ui-muted"}`}>{rupiah(customer.debt || 0)}</td>
                        <td className="pl-2 text-ui-muted"><WriteButton type="button" variant="outline" size="sm" data-testid={`edit-customer-${customer.id}`} aria-label={`Edit pelanggan ${customer.name}`} onClick={event => { event.stopPropagation(); setEditing(customer); }}>Edit</WriteButton></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-ui-muted">{customers.length === 300 ? "Menampilkan hingga 300 pelanggan. Gunakan pencarian untuk mempersempit daftar." : `${customers.length} pelanggan ditampilkan`}</p>
            </>
          )}
        </CardContent>
      </Card>

      {editing && <ContactEditDialog key={editing.id} contact={editing} onClose={() => setEditing(null)} onSaved={updated => {
        setRevision(value => value + 1);
        setProfileCustomer(previous => previous?.id === updated.id ? updated : previous);
        toast.success('Pelanggan diperbarui');
      }} />}
      <Dialog open={open} onOpenChange={(value) => { if (!saving) setOpen(value); }}>
        <DialogContent data-testid="customer-form-dialog" className="w-[calc(100%-2rem)] max-w-md bg-ui-card border-ui-line text-ui-text">
          <DialogHeader>
            <DialogTitle>Tambah Pelanggan</DialogTitle>
            <DialogDescription className="text-ui-muted">Pelanggan ini juga tersedia di kasir dan pencatatan piutang.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="managed-customer-name">Nama pelanggan</Label>
              <Input id="managed-customer-name" data-testid="managed-customer-name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="managed-customer-phone">No. HP (opsional)</Label>
              <Input id="managed-customer-phone" data-testid="managed-customer-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={saving} onClick={() => setOpen(false)}>Batal</Button>
              <WriteButton type="submit" data-testid="save-customer-btn" disabled={saving} className="bg-brand-button hover:bg-brand-hover text-ui-text">{saving ? "Menyimpan…" : "Simpan Pelanggan"}</WriteButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!profileCustomer} onOpenChange={(value) => { if (!value) setProfileCustomer(null); }}>
        <DialogContent data-testid="customer-profile-dialog" className="w-[calc(100%-1rem)] max-w-4xl max-h-[92vh] overflow-y-auto bg-ui-card border-ui-line text-ui-text">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-brand-readable" /> Profil {profileCustomer?.name}</DialogTitle>
            <DialogDescription className="text-ui-muted">{profileCustomer?.phone || "No. HP belum diisi"} · Riwayat belanja dan pencarian transaksi pelanggan.</DialogDescription>
          </DialogHeader>

          {profileError ? <div role="alert" className="rounded-lg border border-danger bg-danger-soft p-3 text-sm text-danger-readable">{profileError}</div> : profile && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-ui-line bg-ui-soft p-3"><CalendarDays className="h-4 w-4 text-brand-readable mb-2" /><div className="text-xs text-ui-muted">Berlangganan sejak</div><div className="font-semibold mt-1">{formatDate(profile.member_since)}</div><div className="text-xs text-ui-muted">{membershipDuration(profile.member_since)}</div></div>
                <div className="rounded-lg border border-ui-line bg-ui-soft p-3"><ShoppingBag className="h-4 w-4 text-brand-readable mb-2" /><div className="text-xs text-ui-muted">Total belanja bersih</div><div className="font-mono font-bold text-brand-readable mt-1">{rupiah(profile.summary.total_spent)}</div>{profile.summary.total_refund > 0 && <div className="text-xs text-ui-muted">Refund {rupiah(profile.summary.total_refund)}</div>}</div>
                <div className="rounded-lg border border-ui-line bg-ui-soft p-3"><ReceiptText className="h-4 w-4 text-brand-readable mb-2" /><div className="text-xs text-ui-muted">Jumlah transaksi</div><div className="text-xl font-bold mt-1">{profile.summary.transaction_count}</div><div className={`text-xs ${profile.customer.debt > 0 ? "text-danger-readable" : "text-ui-muted"}`}>Piutang {rupiah(profile.customer.debt)}</div></div>
              </div>

              <div className="relative">
                <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
                <Input data-testid="customer-history-search" value={profileSearch} onChange={(event) => setProfileSearch(event.target.value)} className="pl-9 bg-ui-card border-ui-line" placeholder="Cari nomor transaksi, nama barang, atau kasir…" />
              </div>

              <div className="flex items-center justify-between gap-3"><h3 className="font-semibold">Riwayat nomor transaksi</h3><span className="text-xs text-ui-muted">{profile.matching_count} ditemukan</span></div>
              {profileLoading ? <p role="status" className="py-6 text-center text-sm text-ui-muted">Mencari riwayat…</p> : profile.history.length === 0 ? <p className="py-6 text-center text-sm text-ui-muted">Riwayat transaksi tidak ditemukan.</p> : (
                <div className="space-y-3">
                  {profile.history.map((transaction) => (
                    <div key={transaction.id} data-testid={`customer-history-${transaction.id}`} className="rounded-lg border border-ui-line overflow-hidden">
                      <div className="flex flex-wrap items-start justify-between gap-3 bg-ui-soft p-3">
                        <div><div className="font-mono font-bold">{transaction.invoice_no}</div><div className="text-xs text-ui-muted mt-1">{formatDate(transaction.created_at, true)} · {transaction.store_name} · {transaction.cashier}</div></div>
                        <div className="text-right"><div className="font-mono font-bold text-brand-readable">{rupiah(transaction.net_total)}</div>{transaction.refund_total > 0 && <div className="text-xs text-danger-readable flex items-center justify-end gap-1"><RotateCcw className="h-3 w-3" /> Refund {rupiah(transaction.refund_total)}</div>}</div>
                      </div>
                      <div className="divide-y divide-ui-line px-3">
                        {transaction.items.map((item, index) => <div key={`${item.product_id}-${index}`} className="flex justify-between gap-3 py-2 text-sm"><span className="min-w-0 break-words">{item.name} <span className="text-ui-muted">×{item.qty}{item.unit ? ` ${item.unit}` : ""}</span>{item.refunded_qty > 0 && <span className="ml-2 text-xs text-danger-readable">{item.refunded_qty} sudah direfund</span>}</span><span className="font-mono whitespace-nowrap">{rupiah(item.qty * item.price)}</span></div>)}
                      </div>
                      <div className="border-t border-ui-line p-2 flex justify-end">
                        <Button type="button" variant="outline" className="h-9 gap-2" onClick={() => navigate(`/transactions?transaction_id=${encodeURIComponent(transaction.id)}`)}><ExternalLink className="h-3.5 w-3.5" /> Buka transaksi / tukar barang</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!profileSearch && profile.matching_count > profile.history.length && <p className="text-xs text-center text-ui-muted">Menampilkan 100 transaksi terbaru. Gunakan pencarian untuk menemukan transaksi lama.</p>}
            </div>
          )}
          {!profile && !profileError && <p role="status" className="py-10 text-center text-sm text-ui-muted">Memuat profil pelanggan…</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}
